import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { useEffect, useState } from 'react';
import regex from '../../../config/regex';
import jsonPost from '../../../util/jsonPost';
import Button from '../Atoms/Button';
import Checkbox from '../Atoms/Checkbox';
import Input from '../Atoms/Input';
import Modal from '../Core/Modal';

export const abandonBasketModalLabelProps = PropTypes.shape({
  bodyText: PropTypes.string,
  closeButtonText: PropTypes.string,
  continueAsGuestLinkText: PropTypes.string,
  emailAddressInvalidValidationText: PropTypes.string,
  emailAddressLabelText: PropTypes.string,
  emailAddressRequiredValidationText: PropTypes.string,
  enabledMarketingSignUp: PropTypes.bool,
  marketingSignUpText: PropTypes.string,
  submitButtonText: PropTypes.string,
  title: PropTypes.string,
});

const propTypes = exact({
  bookNowUrl: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  labels: abandonBasketModalLabelProps,
  onClose: PropTypes.func.isRequired,
  submitApi: PropTypes.string.isRequired,
  tourKey: PropTypes.string.isRequired,
});

const defaultProps = {
  isOpen: false,
  labels: {
    bodyText: '',
    closeButtonText: '',
    continueAsGuestLinkText: '',
    emailAddressInvalidValidationText: '',
    emailAddressLabelText: '',
    emailAddressRequiredValidationText: '',
    enabledMarketingSignUp: false,
    marketingSignUpText: '',
    submitButtonText: '',
    title: '',
  },
};

function AbandonBasketModal({ bookNowUrl, isOpen, labels, onClose, submitApi, tourKey }) {
  const [emailAddress, setEmailAddress] = useState('');
  const [isMarketingSignUpChecked, setIsMarketingSignUpChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('active-modal');
    } else {
      document.body.classList.remove('active-modal');
    }

    return () => {
      document.body.classList.remove('active-modal');
    };
  }, [isOpen]);

  function onClickClose() {
    setEmailAddress('');
    setIsMarketingSignUpChecked(false);
    setErrorMessage(null);
    onClose();
  }

  function onClickProceed() {
    if (emailAddress === '') {
      setErrorMessage(labels.emailAddressRequiredValidationText);
      return;
    }

    if (!regex.email.test(emailAddress)) {
      setErrorMessage(labels.emailAddressInvalidValidationText);
      return;
    }

    setIsLoading(true);

    const options = {
      email: emailAddress,
      tourId: tourKey,
      marketingSignUp: isMarketingSignUpChecked,
    };

    jsonPost(submitApi, options, () => {
      setIsLoading(false);
      window.location.href = bookNowUrl;
      setErrorMessage(null);
    });
  }

  return (
    <Modal handleModalClose={() => onClose()} modalIsOpen={isOpen} title={labels.title}>
      <div className="AbandonBasketModal">
        <div className="AbandonBasketModal_body">
          <p>{labels.bodyText}</p>
          <fieldset>
            <h4>{labels.emailAddressLabelText}</h4>
            <Input
              fieldName="emailAddress"
              fieldValue={emailAddress}
              handleChange={(e) => setEmailAddress(e.target.value)}
              inputType="email"
              isInvalid={!!errorMessage}
              isRequired
              modifier="ghost"
              placeholderText={labels.emailAddressLabelText}
            />
            {errorMessage && <p className="AbandonBasketModal_error">{errorMessage}</p>}
          </fieldset>

          {labels.enabledMarketingSignUp && (
            <fieldset>
              <Checkbox
                fieldID="isMarketingSignUpChecked"
                fieldName="isMarketingSignUpChecked"
                fieldValue="false"
                handleChange={(e) => setIsMarketingSignUpChecked(e.target.checked)}
                isChecked={isMarketingSignUpChecked}
                inputType="checkbox"
                isRequired
                text={labels.marketingSignUpText}
              />
            </fieldset>
          )}
        </div>
        <div className="AbandonBasketModal_footer">
          <Button
            blockName="AbandonBasketModal_link"
            elementType="link"
            text={labels.continueAsGuestLinkText}
            url={bookNowUrl}
            isLoading={isLoading}
            modifier="outline"
          />
          <div className="AbandonBasketModal_buttons">
            <Button
              blockName="AbandonBasketModal_button"
              text={labels.closeButtonText}
              isLoading={isLoading}
              handleClick={() => onClickClose()}
              modifier="outline"
            />
            <Button
              blockName="AbandonBasketModal_button"
              text={labels.submitButtonText}
              isLoading={isLoading}
              isDisabled={labels.enabledMarketingSignUp && !isMarketingSignUpChecked}
              handleClick={() => onClickProceed()}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}

AbandonBasketModal.propTypes = propTypes;
AbandonBasketModal.defaultProps = defaultProps;

export default AbandonBasketModal;
